import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, Trash2, UserPlus, RotateCcw } from 'lucide-react';
import Routes from '@/router/routes';
import {
  useWorkspace,
  useWorkspaceMembers,
  useWorkspaceProjects,
  useRemoveWorkspaceMember,
  useUpdateWorkspaceMemberRole,
  useAddWorkspaceMember,
  useRestoreWorkspace,
} from '@/hooks/api/useWorkspaces';
import { WorkspaceMember } from '@/api/workspaces';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { AddWorkspaceMemberDialog } from '@/components/dialogs/AddWorkspaceMemberDialog';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { formatDate, formatDateTime } from '@/lib/date';

export default function SuperAdminSingleWorkspace() {
  const { workspace_id } = useParams<{ workspace_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Stable filter references — inline objects cause infinite re-renders
  // because React Query treats each new object reference as a key change.
  const defaultFilters = useMemo(() => ({ page: 1, limit: 100 }), []);

  // Queries
  const {
    data: workspace,
    isLoading: workspaceLoading,
    isError: workspaceError,
    refetch: refetchWorkspace,
  } = useWorkspace(workspace_id || '');
  const { data: membersData, isLoading: membersLoading } = useWorkspaceMembers(
    workspace_id || '',
    defaultFilters,
  );
  const { data: projectsData, isLoading: projectsLoading } = useWorkspaceProjects(
    workspace_id || '',
    workspace?.organizationId || '',
    defaultFilters,
  );

  // Mutations
  const removeMember = useRemoveWorkspaceMember();
  const updateMemberRole = useUpdateWorkspaceMemberRole();
  const addMember = useAddWorkspaceMember();
  const restoreWorkspace = useRestoreWorkspace();

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_workspaces.path);
  };

  const handleRestore = () => {
    if (!workspace_id || !workspace?.organizationId) return;
    setConfirmDialog({
      open: true,
      title: 'Restore Workspace',
      description: `Are you sure you want to restore ${workspace.name}? The workspace will be accessible again.`,
      onConfirm: () => {
        restoreWorkspace.mutate(
          { id: workspace_id, organizationId: workspace.organizationId },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleRemoveMember = (member: WorkspaceMember) => {
    if (!workspace_id) return;
    setConfirmDialog({
      open: true,
      title: 'Remove Member',
      description: `Are you sure you want to remove ${member.name || member.email} from this workspace?`,
      onConfirm: () => {
        removeMember.mutate(
          { id: workspace_id, userId: member._id },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
      variant: 'destructive',
    });
  };

  const handleChangeRole = (member: WorkspaceMember, newRole: string) => {
    if (!workspace_id) return;
    updateMemberRole.mutate({ id: workspace_id, userId: member._id, role: newRole });
  };

  const handleAddMember = (userId: string, role: string) => {
    if (!workspace_id) return;
    addMember.mutate(
      { id: workspace_id, userId, role },
      {
        onSuccess: () => {
          setAddMemberDialogOpen(false);
        },
      },
    );
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'Owner';
      case 'CAN_EDIT':
        return 'Can Edit';
      case 'CAN_VIEW':
        return 'Can View';
      default:
        return role;
    }
  };

  if (workspaceLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (workspaceError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetchWorkspace()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="flex flex-col gap-6">
        <Button variant="ghost" onClick={handleBack} className="w-fit">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Workspaces
        </Button>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-red-600">Workspace not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'Workspaces', path: Routes.super_admin_dashboard_workspaces.path },
          { label: workspace.name },
        ]}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBack} size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground">{workspace.name}</h1>
              {workspace.deletedAt && <Badge variant="deleted">Deleted</Badge>}
            </div>
            <p className="text-muted-foreground mt-1">
              Organization:{' '}
              {workspace.organizationId ? (
                <Link
                  to={`${Routes.super_admin_dashboard_organizations.path}/${workspace.organizationId}`}
                  className="text-blue-600 hover:underline"
                >
                  {workspace.organizationName}
                </Link>
              ) : (
                workspace.organizationName
              )}
            </p>
          </div>
        </div>
        {workspace.deletedAt && (
          <Button onClick={handleRestore} disabled={restoreWorkspace.isPending}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Restore Workspace
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="projects">Projects</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Workspace Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Members</span>
                  <span className="font-semibold">{workspace.memberCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Projects</span>
                  <span className="font-semibold">{workspace.projectCount || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Active Projects</span>
                  <span className="font-semibold">{workspace.activeProjectCount || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Workspace Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">Created</span>
                  <p className="font-medium">{formatDateTime(workspace.createdAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Last Updated</span>
                  <p className="font-medium">{formatDateTime(workspace.updatedAt)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Organization</span>
                  <p className="font-medium">{workspace.organizationName}</p>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
              <h3 className="text-lg font-semibold mb-4">Workspace ID</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">MongoDB ID</span>
                  <p className="font-mono text-sm bg-muted p-2 rounded mt-1 break-all">
                    {workspace._id}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="members" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold">Members</h2>
              <Button onClick={() => setAddMemberDialogOpen(true)} size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Member
              </Button>
            </div>

            {membersLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !membersData || !membersData.data || membersData.data.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No members found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {membersData.data.map((member) => (
                    <TableRow key={member._id}>
                      <TableCell className="font-medium">
                        {[member.name, member.last_name].filter(Boolean).join(' ') || 'N/A'}
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <span className="capitalize">{getRoleDisplayName(member.role)}</span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            member.status === 'active'
                              ? 'active'
                              : member.status === 'pending'
                                ? 'pending'
                                : 'default'
                          }
                        >
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.joinedAt || member.invitedAt)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.role !== 'OWNER' && (
                              <>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>Change Role</DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'CAN_EDIT')}
                                      disabled={member.role === 'CAN_EDIT'}
                                    >
                                      Can Edit
                                      {member.role === 'CAN_EDIT' && ' (current)'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleChangeRole(member, 'CAN_VIEW')}
                                      disabled={member.role === 'CAN_VIEW'}
                                    >
                                      Can View
                                      {member.role === 'CAN_VIEW' && ' (current)'}
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member)}
                              className="text-red-600"
                              disabled={member.role === 'OWNER'}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove Member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="projects" className="mt-6">
          <div className="bg-card rounded-lg shadow-sm border border-border">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold">Projects</h2>
            </div>

            {projectsLoading ? (
              <div className="p-4 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !projectsData || !projectsData.data || projectsData.data.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">No projects found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Responses</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectsData.data.map((project) => (
                    <TableRow key={project._id}>
                      <TableCell>
                        <Link
                          to={`${Routes.super_admin_dashboard_projects.path}/${project._id}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {project.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            project.status === 'active'
                              ? 'active'
                              : project.status === 'completed'
                                ? 'success'
                                : 'default'
                          }
                        >
                          {project.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{project.responseCount || 0}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(project.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <AddWorkspaceMemberDialog
        open={addMemberDialogOpen}
        onOpenChange={setAddMemberDialogOpen}
        onAdd={handleAddMember}
        loading={addMember.isPending}
        organizationId={workspace.organizationId}
        existingMemberIds={membersData?.data?.map((m) => m._id) ?? []}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={removeMember.isPending || restoreWorkspace.isPending}
      />
    </div>
  );
}
